import React, { useMemo, useState } from 'react';
import type {
  AssignmentContentQuestion,
  AssignmentLessonContentData,
  AssignmentSubmissionInfo,
} from '../../hooks/useCourseApi';
import { submitAssignmentAnswers } from '../../services/courseService';
import { CheckCircle2, Clock } from 'lucide-react';

interface AssignmentLessonContentProps {
  data: AssignmentLessonContentData;
  onSubmitted?: () => void | Promise<void>;
}

function formatDueDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat('ar-EG', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(d);
  } catch {
    return iso;
  }
}

function isGradedStatus(status: string | null | undefined): boolean {
  return String(status ?? '').trim().toLowerCase() === 'graded';
}

function parseSubmissionData(data: AssignmentLessonContentData): AssignmentSubmissionInfo | null {
  const s = data.submission;
  if (!s || typeof s !== 'object' || Array.isArray(s)) return null;
  return s as AssignmentSubmissionInfo;
}

function resolveGraded(
  data: AssignmentLessonContentData,
  submission: AssignmentSubmissionInfo | null
): boolean {
  return isGradedStatus(data.status) || isGradedStatus(submission?.status);
}

/** Max points: before grading use maxGrade ?? grade; when graded, denominator is maxGrade if present. */
function questionDenomPoints(q: AssignmentContentQuestion, graded: boolean): number | undefined {
  if (typeof q.maxGrade === 'number' && !Number.isNaN(q.maxGrade)) return q.maxGrade;
  if (!graded && typeof q.grade === 'number' && !Number.isNaN(q.grade)) return q.grade;
  return undefined;
}

/** Awarded points when assignment status is graded (`grade` on each question). */
function questionEarnedPoints(q: AssignmentContentQuestion, graded: boolean): number | undefined {
  if (!graded || q.grade == null || typeof q.grade !== 'number' || Number.isNaN(q.grade)) return undefined;
  return q.grade;
}

function formatGradeValue(n: number): string {
  return n.toFixed(2);
}

const gradedScoreBadgeClass =
  'inline-flex items-center rounded-full px-3 py-1 text-sm font-bold tabular-nums bg-emerald-100 text-emerald-900 ring-1 ring-emerald-300';

const AssignmentLessonContent: React.FC<AssignmentLessonContentProps> = ({
  data,
  onSubmitted,
}) => {
  const submissionInfo = useMemo(() => parseSubmissionData(data), [data]);

  const graded = useMemo(
    () => resolveGraded(data, submissionInfo),
    [data, submissionInfo]
  );

  const sortedQuestions = useMemo(
    () => [...data.questions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [data.questions]
  );

  const [answers, setAnswers] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    sortedQuestions.forEach((q) => {
      initial[q.id] = '';
    });
    return initial;
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalGradeLabel = useMemo(() => {
    if (graded && submissionInfo != null && typeof submissionInfo.totalGrade === 'number') {
      return submissionInfo.totalGrade.toFixed(2);
    }
    if (typeof data.totalGrade === 'number') return data.totalGrade.toFixed(2);
    return String(data.totalGrade ?? '');
  }, [graded, submissionInfo, data.totalGrade]);

  /** Whole assignment: earned (after grading) vs max — for green badge when Graded */
  const assignmentGradePair = useMemo(() => {
    if (!graded) return null;
    const max =
      typeof data.totalGrade === 'number' && !Number.isNaN(data.totalGrade)
        ? data.totalGrade
        : sortedQuestions.reduce((acc, q) => {
            const m = typeof q.maxGrade === 'number' && !Number.isNaN(q.maxGrade) ? q.maxGrade : 0;
            return acc + m;
          }, 0) || undefined;
    let earned: number | undefined =
      submissionInfo != null && typeof submissionInfo.totalGrade === 'number'
        ? submissionInfo.totalGrade
        : undefined;
    if (earned == null) {
      const sum = sortedQuestions.reduce(
        (acc, q) => acc + (typeof q.grade === 'number' && !Number.isNaN(q.grade) ? q.grade : 0),
        0
      );
      earned = sum > 0 ? sum : undefined;
    }
    if (earned == null || max == null) return null;
    return { earned, max };
  }, [graded, data.totalGrade, submissionInfo, sortedQuestions]);

  if (data.hasSubmitted) {
    const submissionFeedback =
      submissionInfo?.feedback != null && String(submissionInfo.feedback).trim() !== ''
        ? String(submissionInfo.feedback)
        : null;

    return (
      <div className="bg-white rounded-2xl p-4 sm:p-8 shadow-lg space-y-6" dir="rtl">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 border-b border-slate-100 pb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900">{data.lessonName}</h2>
            <p className="text-sm text-slate-500 mt-1">
              آخر موعد للتسليم:{' '}
              <span className="font-medium text-slate-700">{formatDueDate(data.dueDate)}</span>
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {graded && assignmentGradePair ? (
                <>
                  <span className="text-sm text-slate-600">مجموع الدرجات</span>
                  <span className={gradedScoreBadgeClass}>
                    {formatGradeValue(assignmentGradePair.earned)} /{' '}
                    {formatGradeValue(assignmentGradePair.max)}
                  </span>
                </>
              ) : (
                <p className="text-sm text-slate-600">
                  {graded ? 'مجموع الدرجات: ' : 'الدرجة الكلية (الحد الأقصى): '}
                  <span className="font-semibold text-slate-900">{totalGradeLabel}</span>
                </p>
              )}
            </div>
          </div>
          {graded ? (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200 w-fit">
              <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" aria-hidden />
              تم التصحيح
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold bg-orange-50 text-orange-800 ring-1 ring-orange-200 w-fit">
              <Clock className="w-4 h-4 shrink-0 text-orange-600" aria-hidden />
              تم التسليم
            </span>
          )}
        </div>

        {graded && submissionFeedback ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/90 px-4 py-3">
            <p className="text-xs font-semibold text-emerald-800 mb-1">تقييم عام على الواجب</p>
            <p className="text-sm text-emerald-950 whitespace-pre-wrap leading-relaxed">
              {submissionFeedback}
            </p>
          </div>
        ) : null}

        <div className="space-y-4">
          {sortedQuestions.map((q, idx) => {
            const denom = questionDenomPoints(q, graded);
            const earned = questionEarnedPoints(q, graded);
            const questionPair =
              graded && earned != null && denom != null
                ? { earned, max: denom }
                : null;
            const pendingMaxText = !graded && denom != null ? String(denom) : null;

            return (
              <div
                key={q.id}
                className={`rounded-xl border p-4 sm:p-5 space-y-3 ${
                  graded ? 'border-emerald-100 bg-emerald-50/40' : 'border-slate-100 bg-slate-50/60'
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-slate-800">السؤال {idx + 1}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    {questionPair ? (
                      <span className={gradedScoreBadgeClass}>
                        {formatGradeValue(questionPair.earned)} / {formatGradeValue(questionPair.max)}
                      </span>
                    ) : graded && earned != null ? (
                      <span className={gradedScoreBadgeClass}>{formatGradeValue(earned)}</span>
                    ) : q.type === 'OpenQuestion' && pendingMaxText != null ? (
                      <span className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold bg-slate-200 text-slate-700">
                        من {pendingMaxText} درجة
                      </span>
                    ) : null}
                    {q.type !== 'OpenQuestion' ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-800">
                        {q.type}
                      </span>
                    ) : null}
                  </div>
                </div>
                <p className="text-slate-900 leading-relaxed">{q.text}</p>

                {q.answerText != null && String(q.answerText).trim() !== '' ? (
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5">
                    <p className="text-xs font-semibold text-slate-500 mb-1">إجابتك</p>
                    <p className="text-slate-900 whitespace-pre-wrap text-sm leading-relaxed">
                      {q.answerText}
                    </p>
                  </div>
                ) : null}

                {graded && q.feedback != null && String(q.feedback).trim() !== '' ? (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50/80 px-3 py-2.5">
                    <p className="text-xs font-semibold text-emerald-800 mb-1 flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" aria-hidden />
                      ملاحظات المعلم
                    </p>
                    <p className="text-emerald-950 text-sm whitespace-pre-wrap leading-relaxed">
                      {q.feedback}
                    </p>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        {submissionInfo ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2 text-sm text-slate-700">
            <h3 className="text-sm font-semibold text-slate-800 font-cairo">تفاصيل التسليم</h3>
            {submissionInfo.submittedAt ? (
              <p>
                <span className="font-medium text-slate-800">تاريخ الإرسال: </span>
                {formatDueDate(submissionInfo.submittedAt)}
              </p>
            ) : null}
            {graded && submissionInfo.gradedAt ? (
              <p>
                <span className="font-medium text-slate-800">تاريخ التصحيح: </span>
                {formatDueDate(submissionInfo.gradedAt)}
              </p>
            ) : null}
            {graded && submissionInfo.gradedByUserName ? (
              <p>
                <span className="font-medium text-slate-800">المصحح: </span>
                {submissionInfo.gradedByUserName}
              </p>
            ) : null}
          </div>
        ) : typeof data.submission === 'string' && data.submission.trim() !== '' ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-2">تفاصيل التسليم</h3>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{data.submission}</p>
          </div>
        ) : null}
      </div>
    );
  }

  const handleSubmit = async () => {
    setError(null);
    const missing = sortedQuestions.filter(
      (q) => q.type === 'OpenQuestion' && !String(answers[q.id] ?? '').trim()
    );
    if (missing.length > 0) {
      setError('يرجى الإجابة على جميع الأسئلة المفتوحة قبل الإرسال.');
      return;
    }

    const payload = sortedQuestions.map((q) => ({
      questionId: q.id,
      answerText: String(answers[q.id] ?? '').trim(),
    }));

    setSubmitting(true);
    try {
      await submitAssignmentAnswers(data.id, payload);
      await onSubmitted?.();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'فشل إرسال الواجب');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-4 sm:p-8 shadow-lg space-y-6" dir="rtl">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 border-b border-slate-100 pb-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900">{data.lessonName}</h2>
          <p className="text-sm text-slate-500 mt-1">
            آخر موعد للتسليم:{' '}
            <span className="font-medium text-slate-700">{formatDueDate(data.dueDate)}</span>
          </p>
          <p className="text-sm text-slate-500">الدرجة الكلية: {totalGradeLabel}</p>
        </div>
      </div>

      <div className="space-y-6">
        {sortedQuestions.map((q, idx) => {
          const denom = questionDenomPoints(q, false);
          return (
            <div key={q.id} className="rounded-xl border border-slate-200 p-4 sm:p-5 space-y-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm font-semibold text-slate-800">
                  السؤال {idx + 1}
                  {q.type === 'OpenQuestion' && denom != null ? (
                    <span className="text-slate-400 font-normal mr-1">(من {denom} درجة)</span>
                  ) : null}
                </p>
                {q.type !== 'OpenQuestion' ? (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-800">
                    {q.type}
                  </span>
                ) : null}
              </div>
              <p className="text-slate-900 leading-relaxed">{q.text}</p>

              {q.type === 'OpenQuestion' ? (
                <textarea
                  className="w-full min-h-[140px] rounded-xl border border-slate-200 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-y"
                  placeholder="اكتب إجابتك هنا…"
                  value={answers[q.id] ?? ''}
                  onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                  disabled={submitting}
                />
              ) : (
                <p className="text-sm text-slate-500">نوع السؤال غير مدعوم في الواجهة بعد؛ راجع المعلم.</p>
              )}
            </div>
          );
        })}
      </div>

      {error ? (
        <div className="rounded-xl bg-red-50 text-red-800 text-sm px-4 py-3 border border-red-100">{error}</div>
      ) : null}

      <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="px-6 py-2.5 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? 'جاري الإرسال…' : 'إرسال الواجب'}
        </button>
      </div>
    </div>
  );
};

export default AssignmentLessonContent;
